<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { useMachine } from "@xstate/vue"
import { storeToRefs } from "pinia"
import { createFormPaymentMachine } from "@modules/abonnement/form-payment.machine"
import { useSubscriptionFormStore } from "@modules/abonnement/stores/subscription-form.store"
import type {
    FormPaymentUserForm,
    FormPaymentCompanyForm,
} from "@modules/abonnement/types/subscription"
import type { SubscriptionFormPersisted } from "@modules/abonnement/stores/subscription-form.store"

// ================================
// HELPERS
// ================================

/** True si le brouillon contient au moins une donnée saisie par l'utilisateur (sinon on n'affiche pas la modal reprise). */
function hasUserEnteredData(draft: SubscriptionFormPersisted): boolean {
    if (draft.step !== "FormPaymentIntro") {
        return true
    }
    const u = draft.introFormUser
    const c = draft.introFormCompany
    const hasUser =
        (u?.firstname?.trim() ?? "") !== "" ||
        (u?.lastname?.trim() ?? "") !== "" ||
        (u?.username?.trim() ?? "") !== "" ||
        (u?.phone?.trim() ?? "") !== ""
    const hasCompany = (c?.name?.trim() ?? "") !== ""
    return hasUser || hasCompany
}

// ================================
// COMPOSABLES (route + store)
// ================================
const route = useRoute()
const subscriptionFormStore = useSubscriptionFormStore()
const {
    fetchStripeOffers,
    setPaymentContext,
    setOfferSourceSelected,
    updatePipedriveDealForOffer,
} = subscriptionFormStore
const { loadFromLocalStorage, saveToLocalStorage, clearLocalStorage, $reset } =
    subscriptionFormStore
const {
    paymentContext,
    getSourcesPlans,
    getOffersFormatted,
    offerSourceSelected,
    isFetchingUpdatePipedriveDealForOffer,
    hasErrorUpdatePipedriveDealForOffer,
    errorUpdatePipedriveDealForOffer,
} = storeToRefs(subscriptionFormStore)

// ================================
// MACHINE
// ================================
const machineInput = { getRoute: () => route }
const machine = createFormPaymentMachine(machineInput)
const { snapshot, send } = useMachine(machine, { input: machineInput })

// ================================
// STATE (dialog reprise)
// ================================
const showResumeDialog = ref(false)
/** Étape restaurée depuis le brouillon (pour RESTORE après choix "Reprendre"). */
const restoredStep = ref<
    | "FormPaymentIntro"
    | "FormPaymentOffer"
    | "FormPaymentSources"
    | "FormPaymentRecap"
    | null
>(null)

// ================================
// COMPUTED
// ================================
const currentState = computed(() => (snapshot.value?.value as string) ?? "idle")

/** Index d'étape pour le header (0 = informations, 1 = offre, 2 = sources, 3 = récap / succès / échec). */
const headerStepIndex = computed(() => {
    const s = currentState.value
    if (s === "FormPaymentIntro") return 0
    if (s === "FormPaymentOffer") return 1
    if (s === "FormPaymentSources") return 2
    return 3
})

/** Modèle du header (étape + indicateur d'erreur). */
const headerModelValue = computed(() => ({
    step: headerStepIndex.value,
    error: currentState.value === "FormPaymentFailure",
}))

const isIntro = computed(() => currentState.value === "FormPaymentIntro")
const isOffer = computed(() => currentState.value === "FormPaymentOffer")
const isSources = computed(() => currentState.value === "FormPaymentSources")
const isRecap = computed(() => currentState.value === "FormPaymentRecap")
const isSuccess = computed(() => currentState.value === "FormPaymentSuccess")
const isFailure = computed(() => currentState.value === "FormPaymentFailure")

// ================================
// WATCHERS
// ================================

/** Défaut plan sources (premier) à l’entrée sur l’étape sources. */
watch(
    [currentState, offerSourceSelected, getSourcesPlans],
    () => {
        if (currentState.value !== "FormPaymentSources") return
        if (offerSourceSelected.value) return
        const plans = getSourcesPlans.value
        if (plans.length) {
            setOfferSourceSelected(plans[0].id)
        }
    },
    { immediate: true }
)

/** Persistance du brouillon à chaque changement d'étape (sauf Success/Failure). En cas de succès on efface le brouillon ; en cas d'échec on le garde pour permettre de revenir au récap. */
watch(currentState, (step) => {
    if (
        step === "FormPaymentSuccess" ||
        step === "FormPaymentFailure" ||
        step === "idle"
    ) {
        if (step === "FormPaymentSuccess") {
            clearLocalStorage()
        }
        return
    }
    saveToLocalStorage(step)
})

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    const draft = typeof window !== "undefined" ? loadFromLocalStorage() : null
    if (!getOffersFormatted.value.length) {
        fetchStripeOffers()
    }
    const status = route.query.status as string | undefined
    if (status === "success" || status === "failure") {
        send({ type: "REDIRECTION" })
        return
    }
    if (draft?.step) {
        if (!hasUserEnteredData(draft)) {
            clearLocalStorage()
            send({ type: "REDIRECTION" })
            return
        }
        restoredStep.value = draft.step
        showResumeDialog.value = true
        return
    }
    send({ type: "REDIRECTION" })
})

// ================================
// INTERACTIONS
// ================================
function onSubmit(payload: {
    alreadyExist: false
    user: FormPaymentUserForm & { phone: string }
    company: FormPaymentCompanyForm
}): void {
    setPaymentContext({
        user: payload.user as unknown as Record<string, unknown>,
        company: payload.company as unknown as Record<string, unknown>,
        pipedrive: paymentContext.value?.pipedrive,
    })
    send({ type: "NEXT" })
}

function onUpdatePipedrive(payload: Record<string, unknown>): void {
    setPaymentContext({
        user: paymentContext.value?.user,
        company: paymentContext.value?.company,
        pipedrive: payload,
    })
}

/** Au clic « Continuer » sur l'étape offre : met à jour le deal Pipedrive avec l'offre choisie (ex. EA), puis passe à l'étape sources. */
async function onOfferNext(): Promise<void> {
    const pipedrive = paymentContext.value?.pipedrive as
        | { contactId?: number }
        | undefined
    if (pipedrive?.contactId != null) {
        try {
            await updatePipedriveDealForOffer()
        } catch {
            return
        }
        if (hasErrorUpdatePipedriveDealForOffer.value) {
            return
        }
    }
    send({ type: "SUBMIT" })
}

function onPlanBack(): void {
    send({ type: "PREVIOUS" })
}

function onSourcesBack(): void {
    send({ type: "BACK" })
}

function onSourcesNext(): void {
    send({ type: "NEXT" })
}

function onRecapBack(): void {
    send({ type: "BACK" })
}

function onModify(): void {
    send({ type: "MODIFY" })
}

function onFailureBack(): void {
    send({ type: "BACK" })
}

/** L'utilisateur choisit de reprendre le parcours (dialog reprise). */
function onResumeDialogResume(): void {
    const step = restoredStep.value
    if (step) {
        send({ type: "RESTORE", step })
        restoredStep.value = null
    }
}

/** L'utilisateur choisit de recommencer (dialog reprise). */
async function onResumeDialogRestart(): Promise<void> {
    clearLocalStorage()
    $reset()
    await fetchStripeOffers()
    send({ type: "REDIRECTION" })
    restoredStep.value = null
}
</script>

<template>
    <div class="subscription-form max-w-screen-2xl flex flex-col gap-8">
        <!-- Header réel (masqué quand la dialog reprise est affichée) -->
        <SubscriptionFormHeader
            v-if="!showResumeDialog"
            class="w-full"
            :model-value="headerModelValue"
        />

        <!-- Skeleton header + contenu quand la dialog reprise est affichée -->
        <template v-if="showResumeDialog">
            <BaseSkeleton width="100%" height="20vh" />
            <div
                class="flex flex-col lg:flex-row items-start gap-8 self-stretch w-full"
            >
                <BaseSkeleton width="100%" height="60vh" />
                <BaseSkeleton width="100%" height="70vh" />
            </div>
        </template>

        <!-- 1 – Intro -->
        <SubscriptionFormIntro
            v-if="isIntro"
            class="w-full"
            @submit="onSubmit"
            @update:pipedrive="onUpdatePipedrive"
        />

        <!-- 2 ou 3 – Offre ou Sources + Summary -->
        <div
            v-else-if="isOffer || isSources"
            class="flex flex-col lg:flex-row items-start gap-8 self-stretch w-full"
        >
            <div v-if="isOffer" class="flex flex-col gap-4 w-full">
                <BaseMessage
                    v-if="hasErrorUpdatePipedriveDealForOffer"
                    severity="error"
                >
                    {{
                        errorUpdatePipedriveDealForOffer?.message ??
                        "Une erreur est survenue lors de la mise à jour. Veuillez réessayer."
                    }}
                </BaseMessage>
                <SubscriptionFormOffer
                    :next-loading="isFetchingUpdatePipedriveDealForOffer"
                    @back="onPlanBack"
                    @next="onOfferNext"
                    @modify="onModify"
                />
            </div>
            <SubscriptionFormSources
                v-else
                @back="onSourcesBack"
                @next="onSourcesNext"
                @modify="onModify"
            />
            <SubscriptionFormSummary class="shrink-0" @modify="onModify" />
        </div>

        <!-- 4 – Récap + CTA paiement -->
        <SubscriptionFormRecap
            v-else-if="isRecap"
            class="w-full"
            @back="onRecapBack"
        />

        <!-- Succès -->
        <SubscriptionFormSuccess v-else-if="isSuccess" class="w-full" />

        <!-- Échec -->
        <SubscriptionFormFailure
            v-else-if="isFailure"
            class="w-full"
            @back="onFailureBack"
        />

        <!-- Dialog reprise (brouillon détecté au rechargement) -->
        <SubscriptionFormResumeDialog
            v-model:visible="showResumeDialog"
            @resume="onResumeDialogResume"
            @restart="onResumeDialogRestart"
        />
    </div>
</template>
